// TAWH (total available working hours) for a pay period.
//
// Mirrored in server/utils/tawh.js - keep the two in step. They are not shared
// by import: client and server deploy separately, so the rule is duplicated
// with a pointer on each rather than reached across the build boundary.
//
// This replaced a hand-maintained 24x14 lookup table that only covered 2026.
// See docs/tawh-formula-decision.md for the analysis and the decision.

import { STANDARD_WORK_DAY_HOURS, countWorkingDays, listPayPeriodsInRange } from './payPeriods';

// The granularity TAWH is expressed in. The business works in quarter hours.
export const TAWH_INCREMENT_HOURS = 0.25;

// The formula, and the only definition of it:
//
//   TAWH = working days in the period x 8 x commitment %, rounded UP to 0.25
//
// Rounding up rather than to nearest is deliberate - a commitment is a floor on
// availability, so a partial quarter is owed in full.
//
// `workingDays` is the raw Mon-Fri count. Holidays and company close dates are
// NOT taken out: TAWH is the period's capacity, and time actually taken for a
// closure is credited as a fringe benefit on the other side of the sheet.
//
// The epsilon is what keeps an exact quarter from being pushed up a step by
// floating-point drift - 80 x 0.85 evaluates to 68.00000000000001, which would
// otherwise round up to 68.25.
export const tawhHoursForPeriod = (workingDays, commitmentPercent) => {
  const days = Number(workingDays);
  const percent = Number(commitmentPercent);
  if (!Number.isFinite(days) || days <= 0) return 0;
  if (!Number.isFinite(percent) || percent <= 0) return 0;

  const exact = days * STANDARD_WORK_DAY_HOURS * (percent / 100);
  const increments = Math.ceil(exact / TAWH_INCREMENT_HOURS - 1e-9);
  return increments * TAWH_INCREMENT_HOURS;
};

// The same pay periods the range covers, each with the TAWH it works out to.
// Kept as a list rather than a total because the hours-to-work table shows the
// periods one per row, and because the sum has to be of per-period results:
// rounding the whole span at once is not the same number.
//
// `commitmentPercent` is a number, or (period) => number when the commitment
// changes across the range - each period is measured at its own.
export const tawhPeriodsInRange = (startIso, endIso, commitmentPercent) =>
  listPayPeriodsInRange(startIso, endIso).map((period) => {
    const workingDays = countWorkingDays(period.startDate, period.endDate);
    const percent = typeof commitmentPercent === 'function' ? commitmentPercent(period) : commitmentPercent;
    return {
      ...period,
      workingDays,
      totalHours: workingDays * STANDARD_WORK_DAY_HOURS,
      tawhPercent: percent,
      tawhHours: tawhHoursForPeriod(workingDays, percent),
    };
  });

// Hours to work over a whole range at the given commitment. Null when the range
// covers no complete pay period, which is a "nothing to measure against" rather
// than a target of zero.
export const tawhHoursForRange = (startIso, endIso, commitmentPercent) => {
  const periods = tawhPeriodsInRange(startIso, endIso, commitmentPercent);
  if (periods.length === 0) return null;
  return periods.reduce((sum, period) => sum + period.tawhHours, 0);
};
